import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  GraduationCap, CheckCircle2, ArrowRight, Award, BookOpen,
  Globe2, ScrollText, Sparkles, ShieldCheck,
} from "lucide-react";

export default function DiplomaPage() {
  const { lang } = useLang();
  const fr = lang === "fr";

  return (
    <div className="bg-paper relative overflow-hidden" data-testid="diploma-page">
      {/* Decorative brand blobs */}
      <div className="brand-blob" style={{ background: "hsl(var(--brand-mint))", width: 320, height: 320, top: 80, left: -120 }} />
      <div className="brand-blob" style={{ background: "hsl(var(--brand-yellow))", width: 260, height: 260, top: 700, right: -80, opacity: 0.35 }} />

      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-12 pb-12 lg:pt-16 lg:pb-16">
          <div
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-6 font-display font-semibold tracking-tight text-base sm:text-lg lg:text-xl"
            data-testid="diploma-overline"
          >
            <span className="text-accent uppercase tracking-[0.15em]">{fr ? "Le diplôme" : "The diploma"}</span>
            <span className="text-muted-foreground/50">·</span>
            <span className="text-foreground/80">
              {fr ? "American High School Diploma accrédité Cognia" : "Cognia-accredited American High School Diploma"}
            </span>
          </div>

          <h1
            data-testid="diploma-title"
            className="font-display font-bold tracking-tighter text-foreground text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl"
          >
            {fr
              ? "Le diplôme américain en parallèle du Bac français — sans quitter votre lycée actuel."
              : "An American high school diploma alongside the French Bac — without leaving your current school."}
          </h1>

          <p className="mt-6 font-display font-semibold tracking-tight text-foreground/80 text-xl sm:text-2xl leading-snug max-w-3xl">
            {fr
              ? "Seulement 7 crédits à valider avec OFA Lycée sur 2 ans, en complément de votre lycée français."
              : "Only 7 credits to validate with OFA Lycée over 2 years, alongside your French high school."}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/recommender" data-testid="diploma-cta-eligibility">
              <Button size="lg" className="rounded-full px-7">
                <Sparkles className="w-4 h-4 mr-2" />
                {fr ? "Tester l'éligibilité · 2 min" : "Check eligibility · 2 min"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/inquiry" data-testid="diploma-cta-inquiry">
              <Button size="lg" variant="outline" className="rounded-full px-7 border-2">
                {fr ? "Demander des infos" : "Request information"}
              </Button>
            </Link>
          </div>

          {/* Trust badges */}
          <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent/10 text-accent font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" />
              {fr ? "Accrédité Cognia · USA" : "Cognia accredited · USA"}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary">
              <Globe2 className="w-3.5 h-3.5" />
              {fr ? "Reconnu dans 90+ pays" : "Recognized in 90+ countries"}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary">
              <BookOpen className="w-3.5 h-3.5" />
              {fr ? "24 crédits · 17 par le Bac + 7 par OFA" : "24 credits · 17 via Bac + 7 via OFA"}
            </span>
          </div>
        </div>
      </section>

      {/* CREDITS BREAKDOWN — Visual */}
      <section className="relative bg-card border-y border-border" data-testid="diploma-credits-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
          <p className="overline mb-3">{fr ? "Comment ça marche" : "How it works"}</p>
          <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight max-w-3xl">
            {fr ? "24 crédits requis pour le diplôme américain." : "24 credits required for the American diploma."}
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            {fr
              ? "Votre Baccalauréat français en valide 17 automatiquement. Il ne vous reste que 7 crédits à valider avec OFA Lycée pendant vos années de lycée."
              : "Your French Baccalauréat automatically validates 17. Only 7 credits remain — to be earned with OFA Lycée during your high school years."}
          </p>

          {/* Stacked bar */}
          <div className="mt-10">
            <div className="flex h-14 rounded-2xl overflow-hidden shadow-sm">
              <div
                className="flex items-center justify-center text-white font-display font-bold text-sm md:text-base"
                style={{ width: `${(17 / 24) * 100}%`, background: "hsl(var(--accent))" }}
                data-testid="bar-bac"
              >
                {fr ? "17 crédits · Bac français ✓" : "17 credits · French Bac ✓"}
              </div>
              <div
                className="flex items-center justify-center text-primary-foreground font-display font-bold text-sm md:text-base"
                style={{ width: `${(7 / 24) * 100}%`, background: "hsl(var(--primary))" }}
                data-testid="bar-ofa"
              >
                {fr ? "7 OFA Lycée" : "7 OFA Lycée"}
              </div>
            </div>
            <div className="mt-3 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              <span>0</span>
              <span>{fr ? "Total · 24 crédits = 1 crédit par année" : "Total · 24 credits = 1 credit per year"}</span>
              <span>24</span>
            </div>
          </div>
        </div>
      </section>

      {/* TWO COLUMNS — what's validated where */}
      <section className="relative" data-testid="diploma-courses-split">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Bac side */}
            <Card className="border-accent/30 bg-accent/5">
              <CardContent className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-9 h-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-display font-bold">17</span>
                  <h3 className="font-display font-bold text-2xl tracking-tight">
                    {fr ? "Validés par le Bac français" : "Validated by the French Bac"}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {fr ? "Crédits acquis automatiquement à l'obtention du Baccalauréat — aucun travail supplémentaire requis." : "Credits automatically earned upon obtaining the Baccalauréat — no extra work required."}
                </p>
                <ul className="space-y-2 text-sm">
                  {[
                    { fr: "Littérature française", en: "French Literature", credits: 3 },
                    { fr: "Philosophie", en: "Philosophy", credits: 1 },
                    { fr: "Humanités", en: "Humanities", credits: 3 },
                    { fr: "Mathématiques", en: "Mathematics", credits: 3 },
                    { fr: "Sciences", en: "Science", credits: 3 },
                    { fr: "EPS", en: "Physical Education", credits: 1 },
                    { fr: "Arts", en: "Arts", credits: 1 },
                    { fr: "Options / spécialités", en: "Electives", credits: 2 },
                  ].map((c, i) => (
                    <li key={i} data-testid={`bac-course-${i}`} className="flex items-center justify-between gap-3 py-2 border-b border-accent/20 last:border-0">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                        {fr ? c.fr : c.en}
                      </span>
                      <span className="font-display font-bold tabular-nums text-accent text-sm">
                        {c.credits} {fr ? `crédit${c.credits > 1 ? "s" : ""}` : `cr.`}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* OFA side */}
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-7">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">7</span>
                  <h3 className="font-display font-bold text-2xl tracking-tight">
                    {fr ? "À valider avec OFA Lycée" : "To validate with OFA Lycée"}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {fr ? "Cours suivis en ligne en parallèle de votre lycée français. Minimum 4 crédits en 11e/12e ou 2 crédits en 12e seulement." : "Online courses alongside your French school. Minimum 4 credits in 11th/12th OR 2 credits in 12th only."}
                </p>
                <ul className="space-y-2 text-sm">
                  {[
                    { fr: "English Language Arts", en: "English Language Arts", credits: 4, note: fr ? "Sur 4 ans de lycée" : "Over 4 years of high school" },
                    { fr: "Social Studies", en: "Social Studies", credits: 2, note: fr ? "Histoire & culture américaines" : "American history & culture" },
                    { fr: "Introduction to AI", en: "Introduction to AI", credits: 1, note: fr ? "Cours flexible · à suivre à son rythme pendant le lycée" : "Self-paced course during high school" },
                  ].map((c, i) => (
                    <li key={i} data-testid={`ofa-course-${i}`} className="flex items-start justify-between gap-3 py-2.5 border-b border-primary/20 last:border-0">
                      <span className="flex items-start gap-2">
                        <BookOpen className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                        <span>
                          <span className="font-medium">{fr ? c.fr : c.en}</span>
                          <span className="block text-[11px] text-muted-foreground mt-0.5">{c.note}</span>
                        </span>
                      </span>
                      <span className="font-display font-bold tabular-nums text-primary text-sm shrink-0">
                        {c.credits} {fr ? `crédit${c.credits > 1 ? "s" : ""}` : `cr.`}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section className="relative bg-card border-y border-border" data-testid="diploma-eligibility">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
          <p className="overline mb-3">{fr ? "Êtes-vous éligible ?" : "Are you eligible?"}</p>
          <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight max-w-3xl mb-8">
            {fr ? "Conditions d'admission au diplôme." : "Diploma admission requirements."}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                fr: "Élève anglophone natif ou de niveau quasi-natif",
                en: "Native or near-native English speaker",
              },
              {
                fr: "Inscrit à temps plein dans un lycée français",
                en: "Full-time enrollment in a French high school",
              },
              {
                fr: "Minimum 4 crédits en 11e/12e OU 2 crédits en 12e seulement",
                en: "Minimum 4 credits in 11th/12th OR 2 credits in 12th only",
              },
              {
                fr: "Évaluation en anglais à l'entrée du programme",
                en: "English placement evaluation upon entry",
              },
              {
                fr: "Relevé de notes officiel pour transfert de crédits (9e à 12e)",
                en: "Official transcript required for credit transfer (9th–12th)",
              },
              {
                fr: "Note minimum C- (60 %) requise pour transfert de crédit",
                en: "Minimum grade of C- (60%) required for credit transfer",
              },
            ].map((item, i) => (
              <div key={i} data-testid={`elig-${i}`} className="flex items-start gap-3 p-4 rounded-xl border border-border bg-paper">
                <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <span className="text-sm">{fr ? item.fr : item.en}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GRADING SCALE */}
      <section className="relative" data-testid="diploma-grading">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
          <p className="overline mb-3">{fr ? "Notation" : "Grading"}</p>
          <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight max-w-3xl">
            {fr ? "Système de notation américain — barème sur 100." : "American grading system — 100-point scale."}
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            {fr
              ? "Les notes sont attribuées sur une échelle de 100. Un crédit est validé si l'élève obtient A, B ou C-. En cas de F, le crédit n'est pas accordé."
              : "Grades are assigned on a 100-point scale. A credit is granted with A, B, or C-. An F means no credit is awarded."}
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { letter: "A", range: "90–100", color: "hsl(140 60% 45%)" },
              { letter: "B", range: "80–89", color: "hsl(200 70% 50%)" },
              { letter: "C", range: "70–79", color: "hsl(40 90% 55%)" },
              { letter: "D", range: "60–69", color: "hsl(20 80% 55%)" },
              { letter: "F", range: "0–59", color: "hsl(0 70% 55%)" },
            ].map((g) => (
              <div key={g.letter} data-testid={`grade-${g.letter}`} className="rounded-2xl border-2 p-5 text-center" style={{ borderColor: g.color }}>
                <div className="font-display font-bold text-5xl tracking-tighter" style={{ color: g.color }}>{g.letter}</div>
                <div className="mt-1 text-xs text-muted-foreground tabular-nums">{g.range}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            {fr ? "Équivalence des notes pour étudiants d'Amérique du Nord, Asie, Océanie et Europe : " : "Grade equivalence for students from North America, Asia, Oceania and Europe: "}
            <a
              href="https://international.pantheonsorbonne.fr/sites/default/files/2022-04/Tableau_conversion_notes_2.pdf"
              target="_blank" rel="noopener noreferrer"
              className="text-primary font-medium underline hover:text-primary/80 transition-colors"
              data-testid="grade-equivalence-link"
            >
              {fr ? "Tableau de conversion (Panthéon-Sorbonne)" : "Conversion table (Panthéon-Sorbonne)"}
            </a>
          </p>
        </div>
      </section>

      {/* COGNIA TRUST */}
      <section className="relative bg-card border-y border-border" data-testid="diploma-cognia">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-1">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-accent text-accent-foreground mb-4">
                <Award className="w-10 h-10" strokeWidth={1.5} />
              </div>
              <h3 className="font-display font-bold text-3xl tracking-tight">
                {fr ? "Accréditation Cognia" : "Cognia Accreditation"}
              </h3>
            </div>
            <div className="md:col-span-2 space-y-4 text-sm">
              <p>
                {fr
                  ? "OFA Lycée est accrédité par Cognia, organisme américain de référence pour l'éducation. Notre accréditation est renouvelée tous les 5 ans et est valable jusqu'au 30 juin 2027."
                  : "OFA Lycée is accredited by Cognia, the leading American educational accreditation body. Our accreditation is renewed every 5 years and is valid until June 30, 2027."}
              </p>
              <p>
                {fr
                  ? "Cognia regroupe les commissions NCA CASI, NWAC et SACS CASI, et accrédite 40 000 institutions dans plus de 90 pays, scolarisant 18 millions d'élèves dans le monde."
                  : "Cognia unifies the NCA CASI, NWAC and SACS CASI accreditation commissions, serving 40,000 institutions in 90+ countries and 18 million students worldwide."}
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <Metric value="40 000+" label={fr ? "Institutions" : "Institutions"} />
                <Metric value="90+" label={fr ? "Pays" : "Countries"} />
                <Metric value="18 M" label={fr ? "Élèves" : "Students"} />
                <Metric value="2027" label={fr ? "Renouvellement" : "Next renewal"} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative" data-testid="diploma-final-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 text-center">
          <ScrollText className="w-12 h-12 text-primary mx-auto mb-5" strokeWidth={1.5} />
          <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tighter max-w-3xl mx-auto">
            {fr ? "Votre enfant peut viser le diplôme américain." : "Your child can aim for the American diploma."}
          </h2>
          <p className="mt-5 text-muted-foreground max-w-2xl mx-auto">
            {fr
              ? "Faites le test d'éligibilité en 2 minutes, ou contactez-nous pour une question précise sur le diplôme."
              : "Take the 2-minute eligibility test, or contact us for a specific question about the diploma."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/recommender">
              <Button size="lg" className="rounded-full px-7">
                <Sparkles className="w-4 h-4 mr-2" />
                {fr ? "Tester l'éligibilité · 2 min" : "Check eligibility · 2 min"}
              </Button>
            </Link>
            <Link to="/inquiry">
              <Button size="lg" variant="outline" className="rounded-full px-7 border-2">
                {fr ? "Demander des infos" : "Request information"}
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" variant="outline" className="rounded-full px-7 border-2 border-accent text-accent hover:bg-accent hover:text-accent-foreground">
                <GraduationCap className="w-4 h-4 mr-2" />
                {fr ? "S'inscrire au diplôme" : "Apply for the diploma"}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Metric({ value, label }) {
  return (
    <div>
      <div className="font-display font-bold text-2xl tabular-nums text-foreground">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
